import { AccountPlan, BlazemeterUserConfig, Features, UsageLimitStatusType } from 'types/rest/blazeMeterApiResponse';
import _ from 'lodash';
import { removeUrlParam } from 'utils/urlUtils';
import { VIEW_AS_QUERY_PARAM } from 'constants/bzm-constants';
import store from '../store/configureStore';
import { PendoData } from '../helpers/pendoInit';

export const isDevTestFeatureEnabled = (bzmUserConfig: BlazemeterUserConfig | null) => {
  return bzmUserConfig && _.get(bzmUserConfig, 'user.account.features.devTest')
    ? bzmUserConfig.user.account.features.devTest
    : false;
};

export const isBlazeVseFeatureEnabled = (bzmUserConfig: BlazemeterUserConfig | null) => {
  return false;
};

export const isGroupsFeatureEnabled = () => {
  const enableMockServicesGroups = 'blazeUserConfig.blazeUserConfig.user.account.features.enableMockServicesGroups';
  return _.get(store.getState(), enableMockServicesGroups, false);
};

export const isFindAndReserveIntegrationFeatureEnabled = () => {
  const tdmFindAndReserveIntegration =
    'blazeUserConfig.blazeUserConfig.user.account.features.tdmFindAndReserveIntegration';
  return _.get(store.getState(), tdmFindAndReserveIntegration, false);
};

export const isArdBetaFeatureEnabled = () => {
  const ardBeta = 'blazeUserConfig.blazeUserConfig.user.account.features.ardBeta';
  return _.get(store.getState(), ardBeta, false);
};

export const isTestDataOpenAiEnabled = () => {
  const tdmDP = 'blazeUserConfig.blazeUserConfig.user.account.features.testDataOpenAi';
  return _.get(store.getState(), tdmDP, false);
};

export const isTdmRefreshBlazeDataModel = () => {
  const tdmDP = 'blazeUserConfig.blazeUserConfig.user.account.features.tdmRefreshDataModel';
  return _.get(store.getState(), tdmDP, false);
};

/**
 * Get id of user we want to use in view-as.
 */
export const getViewAsUser = () => {
  return _.get(store.getState(), 'blazeUserConfig.viewAsUser');
};

/**
 * Gets account base url
 */
export const getBlazemeterAccountBaseUrl = (excludeWorkspace?: boolean): string => {
  const bzmUserConfig: any = _.get(store.getState(), 'blazeUserConfig.blazeUserConfig');
  if (_.isNil(bzmUserConfig)) {
    return '';
  }
  const accId = bzmUserConfig.user.activeAccountId;
  const wId = bzmUserConfig.user.activeWorkspaceId;
  if (excludeWorkspace === true) {
    return bzmUserConfig.config.blazeMeterAppUrl + `#/settings/accounts/${accId}`;
  } else {
    return bzmUserConfig.config.blazeMeterAppUrl + `#/settings/accounts/${accId}/workspaces/${wId}`;
  }
};

export const getBlazemeterBillingPageUrl = (excludeWorkspace?: boolean): string => {
  return `${getBlazemeterAccountBaseUrl(excludeWorkspace)}/billing`;
};

export const hasUserAdminRoles = (bzmUserConfig: BlazemeterUserConfig) => {
  if (_.isNil(_.get(bzmUserConfig, 'user.hasAdminAccessRoles'))) {
    return false;
  }
  return bzmUserConfig.user.hasAdminAccessRoles;
};

export const getBlazemeterApiUrl = (bzmUserConfig: BlazemeterUserConfig | null): string => {
  let bzmApiUrl: string = _.get(bzmUserConfig, 'config.blazeMeterApiUrl');
  bzmApiUrl = sanitizeUrl(bzmApiUrl);
  return bzmApiUrl;
};

export const getBlazemeterAnalyticsUrl = (bzmUserConfig: BlazemeterUserConfig | null): string => {
  let analyticsBaseUrl: string = _.get(bzmUserConfig, 'config.analyticsUrl', 'https://analytics-qa.blazemeter.net/');
  analyticsBaseUrl = sanitizeUrl(analyticsBaseUrl);
  return `${analyticsBaseUrl}reports/api/v1/`;
};

export const getBlazemeterAppUrl = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.config.blazeMeterAppUrl', '');
};

export const getMaxActionsCount = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.config.maxActionsCount', 10);
};

/**
 * Removes view_as parameter from specified url.
 * @param url
 */
export const sanitizeUrl = (url: string) => {
  //Remove view as parameter if there is any (it will be added by axios interceptor)
  if (url.includes(`?${VIEW_AS_QUERY_PARAM}=`)) {
    url = removeUrlParam(VIEW_AS_QUERY_PARAM, url);
  }

  return url;
};

/**
 * Checks whether read-only flag should be applied. It has to be in the private cloud request
 * while an account has a private data flag enabled.
 * @param bzmUserConfig
 */
export const isReadOnlyInPrivateCloud = (bzmUserConfig: BlazemeterUserConfig | null): boolean => {
  if (bzmUserConfig != null && bzmUserConfig.user && bzmUserConfig.config) {
    const isPrivateCloudRequest: boolean = bzmUserConfig.config.isPrivateCloudRequest || false;
    const isPrivateData: boolean = bzmUserConfig.user.account.features.privateData;

    // Read-only happens only when private
    // Data is on, and it is not a private cloud request
    if (isPrivateData && !isPrivateCloudRequest) {
      return true;
    }
  }
  return false;
};

/**
 * Checks whether private data feature is enabled.
 * @param bzmUserConfig
 */
export const isPrivateDataEnabled = (bzmUserConfig?: BlazemeterUserConfig | null): boolean => {
  if (bzmUserConfig != null && bzmUserConfig.user) {
    const user = bzmUserConfig.user;
    return user.account.features.privateData || false;
  }

  //Get it from the store if not passed in
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.account.features.privateData', false);
};

/**
 * Checks whether user roll has permissions to edit
 * @param bzmUserConfig
 */
export const isRoleWithPermissions = (bzmUserConfig: BlazemeterUserConfig | null): boolean => {
  if (bzmUserConfig != null && bzmUserConfig.user) {
    return bzmUserConfig.user.hasAdminAccessRoles;
  }
  return false;
};

export const getAccountPlan = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.accountPlan') as AccountPlan;
};

export const isFeatureEnabled = (feature: keyof Features) => {
  return _.get(store.getState(), `blazeUserConfig.blazeUserConfig.user.account.features.${feature}`, false);
};

/**
 * Checks whether we are in private cloud.
 * @param bzmUserConfig
 */
export const isPrivateCloud = (bzmUserConfig?: BlazemeterUserConfig | null): boolean => {
  if (bzmUserConfig != null && bzmUserConfig.user && bzmUserConfig.config) {
    const isPrivateCloudRequest: boolean = bzmUserConfig.config.isPrivateCloudRequest || false;
    const isPrivateData: boolean = bzmUserConfig.user.account.features.privateData;

    // Read only happens only when private
    // Data is on and it is not private cloud request
    if (isPrivateData && isPrivateCloudRequest) {
      return true;
    }
  }

  //Get it from store if not passed as parameter
  const isPrivateData = _.get(
    store.getState(),
    'blazeUserConfig.blazeUserConfig.user.account.features.privateData',
    false,
  );
  const isPrivateCloudRequest = _.get(
    store.getState(),
    'blazeUserConfig.blazeUserConfig.config.isPrivateCloudRequest',
    false,
  );

  return isPrivateData && isPrivateCloudRequest;
};

export const getMockServiceUsageStatus = (): UsageLimitStatusType | null => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.config.usageLimit.status', null);
};

export const getBlazemeterMockServicesMaxReplicas = (bzmUserConfig: BlazemeterUserConfig | null): number => {
  let maxReplicas = 1;
  if (bzmUserConfig != null) {
    maxReplicas = bzmUserConfig.user.accountPlan.mockServicesMaxReplicas;
  }
  return maxReplicas;
};

/**
 * Checks if service is in the list of protected services which cannot be renamed / deleted.
 * @param serviceName - Name of the service to check
 * @param bzmUserConfig - Blazemeter user configuration object
 */
export const isServiceProtected = (serviceName: string, bzmUserConfig: BlazemeterUserConfig | null) => {
  if (_.isNil(bzmUserConfig)) {
    return false;
  }

  const protectedServiceNames = _.get(bzmUserConfig, 'config.protectedServiceNames');
  if (protectedServiceNames) {
    return _.includes(protectedServiceNames, serviceName);
  }
  return false;
};

/**
 * Gets active workspace ID
 */
export const getActiveWorkspaceId = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.activeWorkspaceId', null);
};

/**
 * Gets active Account ID
 */
export const getActiveAccountId = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.activeAccountId', null);
};

/**
 * Get AI Consent from user object.
 */
export const getUserAiConsent = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.aiConsent', undefined);
};

/**
 * Get AI Consent from account object.
 */
export const getAccountAiConsent = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.account.aiConsent', null);
};

/**
 * Gets is user Account Owner
 */
export const isUserAccountOwner = () => {
  return _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.user.accountOwner', null);
};

/**
 * Gets active Account ID
 */
export const getBulkLimit = () => {
  const bulkLimit: any = _.get(store.getState(), 'blazeUserConfig.blazeUserConfig.config.bulkLimit', 25);
  return parseInt(bulkLimit);
};

/**
 * Gets active Account ID
 */
export const getPrivateLocationConfigUrl = (): string => {
  const hasAdminAccessRoles: any = _.get(
    store.getState(),
    'blazeUserConfig.blazeUserConfig.user.hasAdminAccessRoles',
    '',
  );
  if (hasAdminAccessRoles) {
    const personalSettings: any = _.get(
      store.getState(),
      'blazeUserConfig.blazeUserConfig.config.accountSettingsUrl',
      '',
    );
    const settingsUrl = personalSettings.split('/personal');
    return (
      settingsUrl[0] +
      '/accounts/' +
      getActiveAccountId() +
      '/workspaces/' +
      getActiveWorkspaceId() +
      '/private-locations'
    );
  } else {
    return '';
  }
};

/**
 * Gets EU Cluster Enabled flag from plan
 */
export const isEuClusterEnabledInPlan = (): boolean => {
  const clusterEnabled = _.get(
    store.getState(),
    'blazeUserConfig.blazeUserConfig.user.accountPlan.mockServicesEuClusterEnabled',
    true,
  );
  // @ts-ignore
  return clusterEnabled !== false;
};

export const buildPendoData = (bzmConfig: BlazemeterUserConfig): PendoData => {
  return {
    id: String(bzmConfig.user.userId),
    visitorId: String(bzmConfig.user.userId),
    email: bzmConfig.user.email,
    name: bzmConfig.user.displayName,
    full_name: `${bzmConfig.user.firstName} ${bzmConfig.user.lastName}`,
    Role: bzmConfig.user.roles.join(','),
  };
};
